<!--
 * @Description: 平台开票记录（6种按钮组合）
 * @Date: 2026-06-04
-->
<template>
  <XrkTable
    ref="xrkTableRef"
    :getListAjax="getListAjax"
    :searchConfig="searchConfig"
    :tableConfig="tableConfig"
  />
</template>
<script lang="tsx" setup>
import { ref } from 'vue';
import {
  XrkTable,
  XrkTableProps,
  useTableColumn,
  BaseButtonMore,
  useCreateModal,
  BaseButton
} from 'xrk-components';
import { formatter } from '@/utils';
import {
  queryInvoiceList,
  invoiceExport,
  downloadTaxForId,
  jlBatchDownload
} from '@V/invoice/src/data-source/apis';
import { taxPatternMapping } from '@constants/project';
import {
  PLATFORM_BLUE_INVOICE_STATUS,
  PLATFORM_RED_INVOICE_STATUS
} from '@V/invoice/src/constants';
import {
  invoiceOrderNoSearch,
  enterpriseNameSearch,
  supplierNameSearch,
  timeRangeSearch,
  invoiceTypeSearch,
  blueInvoiceStatusSearch,
  redInvoiceStatusSearch,
  updateTimeSearch,
  taskAtSearch
} from '@V/invoice/src/fields/searchs';
import {
  invoiceOrderNoCol,
  enterpriseNameCol,
  supplierNameCol,
  orderTypeCol,
  invoiceTypeCol,
  blueInvoiceStatusCol,
  redInvoiceStatusCol,
  applyTimeCol,
  updateTimeCol,
  invoiceAmountCol,
  taskAtCol
} from '@V/invoice/src/fields/columns';
import InvoiceDetailGtDialog from '../invoice-detail-dialog/invoice-detail-gt-dialog.vue';
import { useBatchDownload } from '@V/invoice/src/hooks';
import { IS_PLATFORM_TYPE_1 } from '@/config';
import type { GtButtonSource } from '@V/invoice/src/types';

const props = withDefaults(
  defineProps<{
    invoiceTabType?: number;
  }>(),
  {
    invoiceTabType: 3
  }
);

const xrkTableRef = ref();
const { selectionField, download } = useBatchDownload({
  xrkTableRef,
  ajax: jlBatchDownload,
  options: {
    passStatus: [2]
  }
});

const getListAjax = async params => {
  return queryInvoiceList({
    ...params,
    type: props.invoiceTabType,
    invoiceBusinessTypeList: [4, 5, 8]
  });
};

const searchConfig = ref<XrkTableProps['searchConfig']>({
  searchColumns: [
    invoiceOrderNoSearch(),
    enterpriseNameSearch(),
    supplierNameSearch(),
    timeRangeSearch(),
    invoiceTypeSearch(),
    blueInvoiceStatusSearch(PLATFORM_BLUE_INVOICE_STATUS),
    redInvoiceStatusSearch(PLATFORM_RED_INVOICE_STATUS),
    updateTimeSearch(),
    taskAtSearch()
  ].filter(Boolean),
  searchBtn: {
    show: true,
    text: '搜索'
  },
  resetBtn: {
    show: true,
    text: '重置'
  },
  otherComponents: [
    {
      render() {
        return (
          <>
            <BaseButton
              type="primary2"
              click={async () => {
                await download();
              }}
            >
              批量导出
            </BaseButton>
          </>
        );
      }
    }
  ]
});

const tableConfig = ref<XrkTableProps['tableConfig']>({
  tableColumns: [
    selectionField(),
    useTableColumn({
      type: 'index',
      label: '序号',
      width: '80px',
      fixed: 'left'
    }),
    invoiceOrderNoCol(),
    enterpriseNameCol(),
    supplierNameCol(),
    orderTypeCol(),
    IS_PLATFORM_TYPE_1 &&
      useTableColumn({
        label: '交易结构',
        prop: 'professionType',
        formatter: ({ cellValue }) =>
          formatter.defaultFormat(
            cellValue,
            () => taxPatternMapping[cellValue]?.label
          )
      }),
    applyTimeCol(),
    invoiceAmountCol(),
    useTableColumn({
      label: '票面金额(元)',
      prop: 'sumAmount',
      formatter: ({ cellValue }) => {
        if (cellValue === null || cellValue === undefined || cellValue === 0) {
          return '--';
        }
        return formatter.fen2yuan(cellValue);
      }
    }),
    invoiceTypeCol(),
    useTableColumn({
      label: '开票类目',
      prop: 'itemName',
      formatter: ({ cellValue }) => {
        if (!cellValue) return '--';
        if (Array.isArray(cellValue)) {
          return cellValue.join(' / ');
        }
        return String(cellValue).split(',').join(' / ');
      }
    }),
    blueInvoiceStatusCol(),
    redInvoiceStatusCol(),
    updateTimeCol(),
    taskAtCol(),

    useTableColumn({
      label: '操作',
      prop: 'handlerCol',
      fixed: 'right',
      width: '200px',
      render: ({ row }) => {
        const BS = row.blueInvoiceStatus;
        const RS = row.redInvoiceStatus;

        // 下载开票信息
        const btnDownloadInfo = {
          content: '下载开票信息',
          click: async () => {
            await invoiceExport({ invoiceId: row.id });
          }
        };

        // 下载发票
        const btnDownloadInvoice = {
          content: '下载发票',
          click: async () => {
            await downloadTaxForId({ invoiceId: row.id });
          }
        };

        // 平台详情弹窗
        const openPlatformDetail = (buttonSource: GtButtonSource) => {
          useCreateModal(InvoiceDetailGtDialog, {
            id: row.id,
            scene: 3,
            buttonSource,
            onSuccess() {
              xrkTableRef.value?.initCurrentList?.();
            }
          });
        };

        // 重新申请（后端暂未提供，先注释），拒绝后直接释放数据变为未开票，与个体不一样
        /*
        const openReapply = () => {
          XrkMessage.success('重新申请已提交');
          xrkTableRef.value.initCurrentList();
        };
        */

        // 按钮独立条件判断
        const btns = [
          // 详情：BS∈[2,12]（已开票/部分开票）
          [2, 12].includes(BS) && {
            content: '详情',
            click: () => openPlatformDetail(11)
          },
          // 重新申请 - BS=3 驳回开票 且 无红票（后端暂未提供）
          /*
          BS === 3 &&
            !RS && {
              content: '重新申请',
              click: () => openReapply()
            },
          */
          // 上传蓝票：[0,11].includes(BS) && (!RS || [7,9].includes(RS))
          [0, 11].includes(BS) &&
            (!RS || [7, 9].includes(RS)) && {
              content: '上传蓝票',
              click: () => openPlatformDetail(1)
            },
          // 审核蓝票：BS === 1
          BS === 1 && {
            content: '审核蓝票',
            click: () => openPlatformDetail(6)
          },
          // 修改上传(蓝)：BS === 3
          BS === 3 && {
            content: '修改上传',
            click: () => openPlatformDetail(3)
          },
          // 补充蓝票：BS === 12 && (!RS || [6,7,8,9,10].includes(RS))
          BS === 12 &&
            (!RS || [6, 7, 8, 9, 10].includes(RS)) && {
              content: '补充蓝票',
              click: () => openPlatformDetail(2)
            },
          // 申请冲红：[2,12].includes(BS) && !RS
          [2, 12].includes(BS) &&
            !RS && {
              content: '申请冲红',
              click: () => openPlatformDetail(8)
            },
          // 上传红票：[2,12].includes(BS) && RS === 4
          [2, 12].includes(BS) &&
            RS === 4 && {
              content: '上传红票',
              click: () => openPlatformDetail(4)
            },
          // 审核红票：RS === 5
          RS === 5 && {
            content: '审核红票',
            click: () => openPlatformDetail(7)
          },
          // 修改上传红票：RS === 7
          [2, 12].includes(BS) &&
            RS === 7 && {
              content: '修改上传红票',
              click: () => openPlatformDetail(5)
            },
          // 补充红票：[2,12].includes(BS) && RS === 10
          [2, 12].includes(BS) &&
            RS === 10 && {
              content: '补充红票',
              click: () => openPlatformDetail(15)
            },
          // 继续冲红：[2,12].includes(BS) && RS === 10
          [2, 12].includes(BS) &&
            RS === 10 && {
              content: '继续冲红',
              click: () => openPlatformDetail(10)
            },
          // 重新申请冲红：RS === 9
          RS === 9 && {
            content: '重新申请冲红',
            click: () => openPlatformDetail(9)
          },
          // 下载开票信息：始终显示
          btnDownloadInfo,
          // 下载发票：[2,12].includes(BS) || [6,10].includes(RS)
          ([2, 12].includes(BS) || [6, 10].includes(RS)) && btnDownloadInvoice
        ].filter(Boolean);

        return (
          <BaseButtonMore
            key={Date.now()}
            maxCount={2}
            style={{ 'justify-content': 'right' }}
            btns={btns}
          ></BaseButtonMore>
        );
      }
    })
  ].filter(Boolean)
});
</script>
